import moment, { Moment } from 'moment-timezone';
import { v4 as uuid } from 'uuid';
import { Rate as BaseRate, RateItem, SaveRate, RateItemType, EditRateItemAttribute } from '@spike/boarding-rule-model';
import { Service } from 'model/Service';
import { BoardingServiceItem } from 'components/Boarding/Components/ServicesSection';

/**
 * Extended Rate interface that includes rateItems array
 * The base Rate model doesn't include this, but it's returned by the API
 */
export interface Rate extends BaseRate {
    rateItems?: RateItem[];
}

/**
 * Class-based form state that encapsulates:
 * - Form field values
 * - Service enrichment (done on initialization)
 * - Edit mode tracking (stores original for diff)
 * - Serialization to SaveRate for API submission
 */
export class RateFormState {
    // Core fields
    name = '';
    startDate: Moment | null = null;
    endDate: Moment | null = null;
    minNights = 1;
    active = true;

    // Client tags
    clientTagsEnabled = false;
    selectedTags: string[] = [];

    // Services with enriched data
    services: BoardingServiceItem[] = [];

    // Edit mode tracking (private - not exposed to form)
    private _originalRate: Rate | null = null;
    private _marketplaceId: number | null = null;

    /**
     * Create empty form state for "Add" mode
     */
    static createEmpty(): RateFormState {
        return new RateFormState();
    }

    /**
     * Create form state from existing Rate for "Edit" mode
     * Services are enriched immediately - no separate enrichment step needed.
     * @param serviceId - when editing from a specific service card, ensures that
     *   service is pre-populated even if rateItems is not available on the rate object.
     */
    static fromRate(rate: Rate, boardingServices: Service[], serviceId?: number): RateFormState {
        const state = new RateFormState();

        // Store original for diff calculation on save
        state._originalRate = rate;
        state._marketplaceId = rate.marketplaceId;

        // Map simple fields directly
        state.name = rate.name || '';
        state.startDate = rate.startDate ? moment(rate.startDate).utc().startOf('day') : null;
        state.endDate = rate.endDate ? moment(rate.endDate).utc().endOf('day') : null;
        state.minNights = rate.minNights;
        state.active = rate.active;

        // Map tags
        state.selectedTags = [...rate.customerTagList];
        state.clientTagsEnabled = rate.customerTagList.length > 0;

        // Map and enrich services in ONE step
        state.services = (rate.rateItems || [])
            .filter(item => item.itemType === RateItemType.SERVICE)
            .map(item => {
                const service = boardingServices.find(s => s.id === item.itemId);
                return {
                    id: uuid(),
                    originalId: item.id,
                    serviceId: item.itemId,
                    serviceName: service?.name || `Service #${item.itemId}`,
                    standardRate: service?.pricing?.fixedPrice?.[0]?.price || 0,
                    newRate: Number(item.amount)
                };
            });

        // Fallback: if rateItems was empty but a serviceId was provided (editing from
        // a service card), pre-populate that service so the form is never blank.
        if (state.services.length === 0 && serviceId !== undefined) {
            const service = boardingServices.find(s => s.id === serviceId);
            if (service) {
                state.services = [
                    {
                        id: uuid(),
                        serviceId: service.id!,
                        serviceName: service.name,
                        standardRate: service.pricing?.fixedPrice?.[0]?.price || 0,
                        newRate: service.pricing?.fixedPrice?.[0]?.price || 0
                    }
                ];
            }
        }

        return state;
    }

    /**
     * Check if this is edit mode
     */
    get isEditMode(): boolean {
        return this._originalRate !== null;
    }

    /**
     * Get the original rate ID (for edit mode)
     */
    get originalId(): number | undefined {
        return this._originalRate?.id;
    }

    /**
     * Convert to SaveRate for API submission
     * Handles add/keep/delete logic internally for edit mode
     */
    toSaveRate(marketplaceId: number): SaveRate {
        const effectiveMarketplaceId = this._marketplaceId || marketplaceId;

        return {
            id: this._originalRate?.id,
            name: crypto.randomUUID(),
            marketplaceId: effectiveMarketplaceId,
            startDate: this.startDate!,
            endDate: this.endDate!,
            minNights: this.minNights,
            active: this.active,
            customerTagList: this.clientTagsEnabled ? this.selectedTags : [],
            rateItemsAttributes: this._buildRateItemsAttributes(effectiveMarketplaceId)
        };
    }

    /**
     * Build rate items with add/keep/delete logic for edit mode
     */
    private _buildRateItemsAttributes(marketplaceId: number): EditRateItemAttribute[] {
        if (!this._originalRate) {
            // Create mode: simple mapping, no IDs
            return this.services.map(service => ({
                marketplaceId,
                itemId: service.serviceId,
                itemType: RateItemType.SERVICE,
                quantity: 1,
                amount: service.newRate ?? 0
            }));
        }

        // Edit mode: calculate diff between original and current
        const result: EditRateItemAttribute[] = [];
        const originalItems = this._originalRate.rateItems || [];

        // Create lookup map for original items
        const originalItemMap = new Map(
            originalItems.filter(item => item.itemType === RateItemType.SERVICE).map(item => [item.itemId, item])
        );

        // Process original items: KEEP or DELETE
        originalItems.forEach(original => {
            if (original.itemType !== RateItemType.SERVICE) return;

            const currentService = this.services.find(s => s.serviceId === original.itemId);

            if (currentService) {
                // KEEP: item still exists, update with new rate
                result.push({
                    id: original.id,
                    marketplaceId: original.marketplaceId,
                    itemId: original.itemId,
                    itemType: RateItemType.SERVICE,
                    quantity: 1,
                    amount: currentService.newRate ?? 0
                });
            } else {
                // DELETE: item was removed, mark for destruction
                result.push({
                    id: original.id,
                    _destroy: true
                });
            }
        });

        // Process current services: ADD new ones
        this.services.forEach(service => {
            if (!originalItemMap.has(service.serviceId)) {
                // ADD: new item without ID
                result.push({
                    marketplaceId,
                    itemId: service.serviceId,
                    itemType: RateItemType.SERVICE,
                    quantity: 1,
                    amount: service.newRate
                });
            }
        });

        return result;
    }

    /**
     * Clone for immutable updates (React state pattern)
     */
    clone(): RateFormState {
        const cloned = new RateFormState();
        cloned.name = this.name;
        cloned.startDate = this.startDate;
        cloned.endDate = this.endDate;
        cloned.minNights = this.minNights;
        cloned.active = this.active;
        cloned.clientTagsEnabled = this.clientTagsEnabled;
        cloned.selectedTags = [...this.selectedTags];
        cloned.services = this.services.map(s => ({ ...s }));
        cloned._originalRate = this._originalRate;
        cloned._marketplaceId = this._marketplaceId;
        return cloned;
    }

    /**
     * Create updated state with new values (immutable pattern for React)
     * Usage: setFormState(prev => prev.with({ startDate: newDate }))
     */
    with(
        updates: Partial<
            Pick<
                RateFormState,
                | 'name'
                | 'startDate'
                | 'endDate'
                | 'minNights'
                | 'active'
                | 'clientTagsEnabled'
                | 'selectedTags'
                | 'services'
            >
        >
    ): RateFormState {
        const cloned = this.clone();
        Object.assign(cloned, updates);
        return cloned;
    }

    /**
     * Returns true if any rate-level fields (dates, minNights, tags) differ from the original.
     * Amount-only changes return false, so no split is needed for those edits.
     */
    hasRateLevelChanges(): boolean {
        if (!this._originalRate) return false;

        const orig = this._originalRate;

        if (!this.startDate || !this.endDate) return false;
        if (!this.startDate.isSame(orig.startDate, 'day')) return true;
        if (!this.endDate.isSame(orig.endDate, 'day')) return true;
        if (this.minNights !== orig.minNights) return true;

        const currentTags = [...(this.clientTagsEnabled ? this.selectedTags : [])].sort();
        const originalTags = [...orig.customerTagList].sort();
        if (currentTags.length !== originalTags.length) return true;
        if (currentTags.some((tag, i) => tag !== originalTags[i])) return true;

        return false;
    }

    /**
     * Build a POST payload for a new rate containing only this service's item.
     * Used when splitting a shared rate on edit.
     */
    toCreateSplitRate(marketplaceId: number): SaveRate {
        const effectiveMarketplaceId = this._marketplaceId || marketplaceId;

        return {
            name: crypto.randomUUID(),
            marketplaceId: effectiveMarketplaceId,
            startDate: this.startDate!,
            endDate: this.endDate!,
            minNights: this.minNights,
            active: this.active,
            customerTagList: this.clientTagsEnabled ? this.selectedTags : [],
            rateItemsAttributes: this.services.map(service => ({
                marketplaceId: effectiveMarketplaceId,
                itemId: service.serviceId,
                itemType: RateItemType.SERVICE,
                quantity: 1,
                amount: service.newRate ?? 0
            }))
        };
    }

    /**
     * Build a PATCH payload to detach this service's item from the original rate.
     * Used when splitting a shared rate on edit.
     */
    toDestroyServiceItemRate(marketplaceId: number): SaveRate {
        const effectiveMarketplaceId = this._marketplaceId || marketplaceId;
        const orig = this._originalRate!;

        const itemsToDestroy: EditRateItemAttribute[] = (orig.rateItems || [])
            .filter(item => this.services.some(s => s.serviceId === item.itemId && s.originalId === item.id))
            .map(item => ({ id: item.id, _destroy: true }));

        return {
            id: orig.id,
            name: crypto.randomUUID(),
            marketplaceId: effectiveMarketplaceId,
            startDate: orig.startDate,
            endDate: orig.endDate,
            minNights: orig.minNights,
            active: orig.active,
            customerTagList: [...orig.customerTagList],
            rateItemsAttributes: itemsToDestroy
        };
    }
}
